import { useMemo } from 'react';
import { CalendarClock, Flag, ClipboardList, ShieldAlert } from 'lucide-react';
import type { ArStats, ArOpenItem, ArCustomerRisk } from '../../types/arDunning';
import { formatMoney, agingBarColor, bucketLabel, riskSortValue, riskDotClass, RISK_LABEL, formatDelayDays } from '../../utils/arDunningFormat';

interface Props {
    stats: ArStats | null;
    items: ArOpenItem[];
    riskByCustomer: Map<string, ArCustomerRisk>;
    onDrillBucket: (bucket: string) => void;
    onDrillCustomer: (customerName: string) => void;
    onGoPromises: () => void;
    onGoDisputes: () => void;
    onGoFollowUps: () => void;
}

export function OverviewTab({ stats, items, riskByCustomer, onDrillBucket, onDrillCustomer, onGoPromises, onGoDisputes, onGoFollowUps }: Props) {
    const maxEur = useMemo(() => Math.max(1, ...(stats?.agingBuckets ?? []).map(b => Math.abs(b.eur))), [stats]);

    // Top overdue customers (client-side view over the loaded set — the authoritative
    // totals are the server-aggregated KPI tiles above).
    const topOverdue = useMemo(() => {
        const map = new Map<string, { name: string; eur: number; maxDays: number; count: number }>();
        for (const i of items) {
            if (i.ItemCategory === 'RibaIssued') continue; // issued RiBa is not dunnable-overdue
            if (i.AgingBucket === 'current' || i.AgingBucket === 'unknown') continue;
            if (i.OpenAmountEUR <= 0) continue;
            const key = i.CustomerID;
            const row = map.get(key) ?? { name: i.CustomerName ?? i.CustomerCode ?? key, eur: 0, maxDays: 0, count: 0 };
            row.eur += i.OpenAmountEUR;
            row.maxDays = Math.max(row.maxDays, i.DaysOverdue);
            row.count += 1;
            map.set(key, row);
        }
        return [...map.values()].sort((a, b) => b.eur - a.eur).slice(0, 10);
    }, [items]);

    // Payment-behaviour risk: counts + the riskiest customers with open exposure.
    const riskCustomers = useMemo(() => [...riskByCustomer.values()], [riskByCustomer]);
    const riskCounts = useMemo(() => {
        const c = { red: 0, amber: 0, green: 0, none: 0 } as Record<string, number>;
        for (const r of riskCustomers) c[r.RiskLevel] = (c[r.RiskLevel] ?? 0) + 1;
        return c;
    }, [riskCustomers]);
    const topRisk = useMemo(
        () => riskCustomers
            .filter(r => r.RiskLevel === 'red' || r.RiskLevel === 'amber')
            .sort((a, b) => riskSortValue(b.RiskLevel, b.RiskScore) - riskSortValue(a.RiskLevel, a.RiskScore))
            .slice(0, 8),
        [riskCustomers],
    );

    return (
        <div className="grid gap-4 lg:grid-cols-2">
            {/* Aging distribution */}
            <div className="rounded-lg border border-slate-200 bg-white p-4">
                <h3 className="mb-3 text-sm font-semibold text-slate-700">Aging distribution <span className="font-normal text-slate-400">(EUR)</span></h3>
                <div className="space-y-2">
                    {(stats?.agingBuckets ?? []).map(b => {
                        const pct = Math.min(100, Math.round((Math.abs(b.eur) / maxEur) * 100));
                        return (
                            <button key={b.bucket} onClick={() => onDrillBucket(b.bucket)} className="block w-full text-left">
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-slate-600">{bucketLabel(b.bucket)}</span>
                                    <span className="text-slate-500">{formatMoney(b.eur)} · {b.count}</span>
                                </div>
                                <div className="mt-0.5 h-2.5 w-full overflow-hidden rounded bg-slate-100">
                                    <div className={`h-full ${agingBarColor(b.bucket)}`} style={{ width: `${pct}%` }} />
                                </div>
                            </button>
                        );
                    })}
                    {(!stats || stats.agingBuckets.length === 0) && <p className="text-sm text-slate-400">No data.</p>}
                </div>
                {stats != null && stats.ribaExposureEUR > 0 && (
                    <p className="mt-3 border-t border-slate-100 pt-2 text-xs text-blue-700">
                        RiBa issued (SBF): <span className="font-semibold">{formatMoney(stats.ribaExposureEUR)}</span> across {stats.ribaItemCount} item{stats.ribaItemCount === 1 ? '' : 's'} — collected by the bank, excluded from aging.
                    </p>
                )}
            </div>

            {/* Action summary + top overdue */}
            <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                    <SummaryCard icon={CalendarClock} tone="text-sky-700" label="Promises" value={stats?.openPromiseCount ?? 0} onClick={onGoPromises} />
                    <SummaryCard icon={Flag} tone="text-amber-700" label="Disputes" value={stats?.openDisputeCount ?? 0} onClick={onGoDisputes} />
                    <SummaryCard icon={ClipboardList} tone="text-slate-700" label="Follow-ups" value={stats?.openFollowUpCount ?? 0} onClick={onGoFollowUps} />
                </div>

                <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <h3 className="mb-3 text-sm font-semibold text-slate-700">Top overdue customers</h3>
                    {topOverdue.length === 0 ? (
                        <p className="text-sm text-slate-400">No overdue receivables.</p>
                    ) : (
                        <div className="space-y-1">
                            {topOverdue.map(c => (
                                <button key={c.name} onClick={() => onDrillCustomer(c.name)} className="flex w-full items-center justify-between rounded px-2 py-1 text-sm hover:bg-slate-50">
                                    <span className="min-w-0 truncate text-slate-700" title={c.name}>{c.name}</span>
                                    <span className="ml-2 flex shrink-0 items-center gap-2">
                                        <span className="text-xs text-rose-600">{c.maxDays}d</span>
                                        <span className="font-medium text-slate-800">{formatMoney(c.eur)}</span>
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Payment-behaviour risk (Phase 7) — spans both columns */}
            <div className="rounded-lg border border-slate-200 bg-white p-4 lg:col-span-2">
                <div className="mb-3 flex items-center justify-between">
                    <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-700"><ShieldAlert className="h-4 w-4 text-rose-600" /> Payment risk</h3>
                    <div className="flex items-center gap-3 text-xs">
                        {(['red', 'amber', 'green', 'none'] as const).map(lvl => (
                            <span key={lvl} className="inline-flex items-center gap-1 text-slate-500">
                                <span className={`h-2.5 w-2.5 rounded-full ${riskDotClass(lvl)}`} /> {RISK_LABEL[lvl]} <span className="font-semibold text-slate-700">{riskCounts[lvl] ?? 0}</span>
                            </span>
                        ))}
                    </div>
                </div>
                {riskCustomers.length === 0 ? (
                    <p className="text-sm text-slate-400">No risk data available.</p>
                ) : topRisk.length === 0 ? (
                    <p className="text-sm text-slate-400">No elevated-risk customers in scope.</p>
                ) : (
                    <div className="grid gap-1 sm:grid-cols-2">
                        {topRisk.map(r => (
                            <button key={r.CustomerID} onClick={() => onDrillCustomer(r.CustomerName ?? r.CustomerCode ?? '')} className="flex w-full items-center justify-between rounded px-2 py-1 text-sm hover:bg-slate-50">
                                <span className="flex min-w-0 items-center gap-2">
                                    <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${riskDotClass(r.RiskLevel)}`} />
                                    <span className="min-w-0 truncate text-slate-700" title={r.CustomerName ?? ''}>{r.CustomerName ?? r.CustomerCode}</span>
                                </span>
                                <span className="ml-2 flex shrink-0 items-center gap-2 text-xs">
                                    {r.AvgDelayDays != null && <span className="text-slate-400">{formatDelayDays(r.AvgDelayDays)}</span>}
                                    <span className="font-medium text-slate-800">{formatMoney(r.OpenEUR)}</span>
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function SummaryCard({ icon: Icon, tone, label, value, onClick }: { icon: typeof CalendarClock; tone: string; label: string; value: number; onClick: () => void }) {
    return (
        <button onClick={onClick} className="rounded-lg border border-slate-200 bg-white p-3 text-left shadow-sm hover:border-indigo-300">
            <div className="flex items-center justify-between">
                <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</span>
                <Icon className={`h-4 w-4 ${tone}`} />
            </div>
            <div className={`mt-1 text-xl font-semibold ${tone}`}>{value}</div>
        </button>
    );
}
